export interface BlogSection {
  heading?: string;
  body: string[];
  bullets?: string[];
}

export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  publishedOn: string;
  readTime: string;
  tags: string[];
  theme: {
    gradient: string;
    accent: string;
  };
  pullQuote?: string;
  sections: BlogSection[];
}

export const blogPosts: BlogPost[] = [
  {
    slug: "why-your-resume-needs-more-than-words",
    title: "Why Your Resume Needs More Than Just Words in the Age of AI",
    description:
      "Everyone is using AI to craft resumes. Here is why your story has to be bigger than keywords and semantic scores.",
    publishedOn: "February 2025",
    readTime: "6 min read",
    tags: ["Future of hiring", "Storytelling", "AI"],
    theme: {
      gradient: "from-sky-50 via-white to-emerald-50",
      accent: "from-sky-500 to-emerald-500",
    },
    pullQuote: "You are more than bullet points. You are a story.",
    sections: [
      {
        body: [
          "Large language models write a huge number of resumes now. People rewrite their resumes for every new job description using tools like GPT, Claude, or Gemini. This has become common and almost expected.",
          "Because of this, matching a resume to a job is no longer about checking if the right keywords are present. Even semantic similarity and all the other fancy algorithms are becoming less helpful. When everyone is using the same tools, the output starts to look the same.",
          'And this is where recruiters are struggling. Two people with similar career paths now end up having resumes that look almost identical. Same format. Same words. Same "strong communication skills." Same "proven track record."',
          "So the question becomes: how does a recruiter tell the difference between two people who look the same on paper? The answer is simple - you need more than just a resume to tell your story.",
        ],
      },
      {
        heading: "Your Story Is Bigger Than One Document",
        body: [
          "A resume is only one dimension of who you are. How you write your resume matters. How you present yourself matters. Your soft skills matter. Your domain knowledge matters. Your ability to answer questions clearly and stay on point matters.",
          "A recruiter wants to know how you think, how you explain your work, and whether you communicate simply and confidently. Can you go deep when needed, or do you get lost in the details? Are you curious? Are you steady under pressure? These things never show up fully in a single PDF.",
        ],
      },
      {
        heading: "More Dimensions. A Fuller Picture.",
        body: [
          "With theResume, we want to bring these hidden dimensions to the surface. Your resume is still important - it is the foundation. But your resume alone should not be the only thing that defines you.",
          "When you use theResume, you can represent yourself through:",
        ],
        bullets: [
          "Your actual work stories, written in simple, powerful language.",
          "Your soft skills, shown through your answers in mock or real AI interviews.",
          "Your communication style, captured through voice, tone, clarity, and structure.",
          "Your depth of knowledge, shown through the way you talk about a topic, not just list it.",
          "Your reasoning, your curiosity, and how you respond when you do not know something yet.",
        ],
      },
      {
        heading: "The Future Belongs to Humans Who Show Themselves Clearly",
        body: [
          "As AI becomes more common, humans who can express themselves better will stand out. Not by using the most perfect words. Not by sounding like a robot. But by being clear, honest, and specific about their experience.",
          "theResume exists to help you do exactly that. To help you show your true strengths in a simple and structured way. To help you go beyond the resume. To help recruiters actually understand who you are - not who a language model wants you to be.",
          "More dimensions. More clarity. More truth. That is the future of hiring. And we are building it with you.",
        ],
      },
    ],
  },
  {
    slug: "more-signals-less-noise-real-skills",
    title: "More Signals, Less Noise: How theRecruit Measures Real Skills",
    description:
      "Resumes alone miss the signals that show who you really are. Here is how we surface the clarity, depth, and confidence behind your story.",
    publishedOn: "February 2025",
    readTime: "7 min read",
    tags: ["Assessment", "Signal vs noise", "AI"],
    theme: {
      gradient: "from-amber-50 via-white to-slate-50",
      accent: "from-amber-500 to-indigo-500",
    },
    pullQuote: "We reduce the noise and capture the signals that make you you.",
    sections: [
      {
        body: [
          "We are moving into a world where a resume is not enough. And honestly, it should not be enough. Real skill lives across many places - in your voice, your clarity, your confidence, your work stories, your depth of knowledge, and even in the way you explain something complex in a simple way.",
          "Most of these things never show up in a PDF. They stay hidden. And this is where theRecruit steps in. We want to reduce the noise and capture the real signals that make you you.",
        ],
      },
      {
        heading: "Your Skill Is Not One-Dimensional",
        body: [
          "Your skills and your story are not limited to bullet points. They are scattered across many dimensions:",
        ],
        bullets: [
          "The way you write your resume",
          "The way you communicate",
          "The way you introduce yourself",
          "The way you answer soft-skill questions",
          "The way you explain hard technical concepts",
          "The depth in your subject knowledge",
          "The clarity in your thinking",
          "The steadiness in your voice",
          "The focus you show when asked something difficult",
        ],
      },
      {
        heading: "We Do Not Just Read What You Say - We Understand What You Mean",
        body: [
          "When you upload a video introduction, our system does not just pick up the words. It understands what you truly want to express. Large language models today can understand meaning, tone, intent, confidence, and clarity. This is a huge shift, and we make full use of it.",
          "With theResume and theRecruit combined, we look at your resume, video introduction, interview answers, job-specific questions, and communication style. Each of these adds a new dimension, a new signal, a new part of your story that normally stays hidden.",
        ],
      },
      {
        heading: "Every Signal Matters",
        body: [
          "Our system breaks down your information using a mix of algorithms, embeddings, scoring models, and structured analysis. This lets us understand things like:",
        ],
        bullets: [
          "How on-point your answers are",
          "How well you explain a hard concept",
          "How confident or unsure you sound",
          "How clearly you communicate",
          "Whether you stay focused or drift",
          "Whether your examples feel real and grounded",
          "Whether your hard skills match what you claim on your resume",
        ],
      },
      {
        heading: "More Signals. Less Noise.",
        body: [
          "Everything you do leaves a signal. Every answer. Every introduction. Every resume edit. Every story you share.",
          "We collect these signals, structure them, and present them back to the recruiter in a clear and meaningful way. No fluff. No noise. No generic buzzwords. Just the real you - amplified.",
          "theRecruit is built to make sure your work speaks louder than keywords. To help you stand out even when everyone looks the same on paper. To bring your hidden dimensions forward. To show who you are, not just what you wrote in a PDF.",
        ],
      },
    ],
  },
];
